import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchExcelAppointments } from "slices/appointmentSlice";
import { changeStatus } from "slices/appointmentSlice";
import { appointmentsSelector } from "slices/appointmentSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";
import { ListLayout } from "components/common/ListLayout";
import { PAGELENGTH } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { selectCustomStyles } from "components/common/Select";
import { getBackgroundStyle } from "components/common/TableColor";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import DateRangeFilter from "components/common/DateRangeFilter";
import { usePermissions } from "layouts/Admin";
import { useForm, Controller } from "react-hook-form";
import { AsyncPaginate } from "react-select-async-paginate";
import moment from "moment";

function ExcelAppointments() {
  const dispatch = useDispatch();
  const { checkPermission } = usePermissions();

  const {
    appointments,
    last_page: totalPages,
    loader,
    total,
    current_page,
    per_page: pageLength,
  } = useSelector(appointmentsSelector);

  const [selectedItems, setSelectedItems] = React.useState([]);
  const [selectAll, setSelectAll] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [activeLegends, setActiveLegends] = useState([0]);
  const [statusValues, setStatusValues] = useState([]);
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const today = moment().format('YYYY-MM-DD');
  const defaultFilters = { pageLength: 100, sortBy: 'date', sortDirection: 'asc', start_date: today, end_date: today };
  const [filters, setFilters] = React.useState(defaultFilters);
  const { control, handleSubmit, reset, setValue, getValues } = useForm({
    defaultValues: {
      start_date: new Date(),
      end_date: new Date(),
      appointment_id: "",
      status: "",
    },
  });
  const [sortConfig, setSortConfig] = React.useState({
    sortBy: "meeting_date",
    sortDirection: "asc",
  });

  const [languageState, setLanguageState] = useState([]);

  React.useEffect(() => {
    dispatch(fetchExcelAppointments(1, defaultFilters));
  }, []);

  useEffect(() => {
    setLanguageState(
      languages?.map((val) => ({
        label: val?.language?.name || "",
        value: val?.language?.id || "",
      }))
    );
  }, [languages]);

  const handleActiveLegend = (id, value) => {
    const buildLegendParams = (statuses) => ({
      ...filters,
      pageLength: 100,
      status: statuses.length ? statuses : undefined,
    });

    if (value === "all") {
      setActiveLegends([id]);
      setStatusValues([]);
      const f = buildLegendParams([]);
      setFilters(f);
      dispatch(fetchExcelAppointments(1, f));
    } else {
      setActiveLegends((prev) => {
        const withoutAll = prev.filter((pid) => pid !== 0);
        let newActiveLegends, newStatuses;
        if (withoutAll.includes(id)) {
          newActiveLegends = withoutAll.filter((pid) => pid !== id);
          newStatuses = statusValues.filter((s) => s !== value);
        } else {
          newActiveLegends = [...withoutAll, id];
          newStatuses = [...statusValues, value];
        }
        if (newActiveLegends.length === 0) {
          newActiveLegends = [0];
          newStatuses = [];
        }
        setStatusValues(newStatuses);
        const f = buildLegendParams(newStatuses);
        setFilters(f);
        dispatch(fetchExcelAppointments(1, f));
        return newActiveLegends;
      });
    }
  };

  const filteredSchools = React.useMemo(
    () =>
      schools?.map((s) => ({
        value: s?.id,
        label: s?.name,
      })),
    [schools]
  );

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true)
    );
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true)
    );
    return {
      options: result?.data?.map((language) => ({
        value: language.id,
        label: language.name,
      })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
      return;
    }
    setSelectAll(true);
    setSelectedItems(
      appointments && appointments?.map((appointment) => appointment.id.toString())
    );
  };

  const handleChange = (e) => {
    const id = e.target.dataset.value;
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item != id)
        : [...selectedItems, id]
    );
  };

  const handleReset = () => {
    setActiveLegends([0]);
    setStatusValues([]);
    setLanguageState([]);
    setSortConfig({ sortBy: "meeting_date", sortDirection: "asc" });
    setFilters(defaultFilters);
    reset({ start_date: new Date(), end_date: new Date(), appointment_id: "", status: "" });
    dispatch(fetchExcelAppointments(1, defaultFilters));
  };

  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0",
    });
    return temp_array;
  }, [legends]);

  //#region TABLE HEAD

  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      const params = { ...filters, sortBy, sortDirection: dir, search: searchTerm, pageLength: 100 };
      setFilters(params);
      dispatch(fetchExcelAppointments(currentPage, params));
    };
    return (
      <tr>
        <th>
          <div className="row_check">
            <span>#</span>
          </div>
        </th>

        <th>
          <TableHeaderItem
            title="appid"
            sortBy="appid"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>

        <th className="col-1">
          <TableHeaderItem
            title="Language"
            sortBy="language"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>
        <th className="col-2">
          <TableHeaderItem
            title="Type"
            sortBy="type"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>

        <th className="col-1">
          <TableHeaderItem
            title="Status"
            sortBy="status"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>

        <th className="col-1">
          <TableHeaderItem
            title="Interpreter"
            sortBy="interpreter"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>
        <th className="col-3">
          <TableHeaderItem
            title="Meeting Date & time"
            sortBy="meeting_date"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>
        <th className="col-2">
          <TableHeaderItem
            title="School"
            sortBy="school"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>

        <th className="col-2">
          <TableHeaderItem
            title="Creation Date"
            sortBy="created_at"
            sortDirection={sortConfig.sortDirection}
            activeSortBy={sortConfig.sortBy}
            callBack={callBack}
          />
        </th>
      </tr>
    );
  }, [selectAll, searchTerm, sortConfig, currentPage, selectedItems, appointments, filters]);

  //#endregion

  //#region TABLE BODY

  const renderTableContent = React.useCallback(() => {
    return (
      appointments &&
      appointments?.map((appointment, index) => (
        <ExcelAppointmentItem
          key={appointment.id}
          appointment={appointment}
          index={index}
          selectedItems={selectedItems}
          handleChange={handleChange}
          current_page={current_page}
          pageLength={pageLength}
          checkPermission={checkPermission}
        />
      ))
    );
  }, [appointments, selectedItems, current_page]);

  //#endregion

  return (
    <>
      <div style={{ margin: "84px 16px 0" }}>
        <div class="row_main_head">
          <h2 class="main_heading">Excel Appointments</h2>
        </div>
        <div className="card_table">
          <div className="button-sm forLegends p-3 gap-1">
            <LegendButton
              activeIds={activeLegends}
              options={filteredStatuses}
              handleClick={handleActiveLegend}
            />
          </div>
          <form
            onSubmit={handleSubmit((values) => {
              const params = {
                pageLength: 100,
                sortBy: 'date',
                sortDirection: 'asc',
                start_date: values.start_date ? moment(values.start_date).format("YYYY-MM-DD") : null,
                end_date: values.end_date ? moment(values.end_date).format("YYYY-MM-DD") : null,
                appointment_id: values.appointment_id || "",
                type: values.type,
                language_ids: values.language_ids || [],
                status: statusValues.length ? statusValues : undefined,
                school_id: values.school_id ?? null,
              };
              setSortConfig({ sortBy: 'meeting_date', sortDirection: 'asc' });
              setFilters(params);
              dispatch(fetchExcelAppointments(1, params));
            })}
            className="search_form_ad"
          >
            <div className="row-primary dahboard_filter appointments">
              <DateRangeFilter control={control} />

              {/* Appointment ID */}
              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input
                        {...field}
                        type="text"
                        className="input-in type_inp"
                        placeholder="Enter ID"
                      />
                    </div>
                  )}
                />
              </div>

              {/* School */}
              <div className="row-primary">
                <h2>School</h2>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{ ...selectCustomStyles }}
                        value={
                          field.value
                            ? filteredSchools?.find((opt) => field.value == opt.value)
                            : ""
                        }
                        loadOptions={filteredSchoolLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => field.onChange(obj?.value)}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>

              {/* Type */}
              <div className="row-primary">
                <h2>Type</h2>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        {...field}
                        styles={selectCustomStyles}
                        options={[
                          { value: 0, label: "Interpreting Request" },
                          { value: 1, label: "After Hours Request" },
                        ]}
                        isClearable
                        placeholder="Select Type"
                        onChange={(option) =>
                          field.onChange(option !== null ? option.value : null)
                        }
                        value={
                          field.value !== null && field.value !== undefined
                            ? [
                                { value: 0, label: "Interpreting Request" },
                                { value: 1, label: "After Hours Request" },
                              ].find((opt) => opt.value === field.value)
                            : null
                        }
                      />
                    </div>
                  )}
                />
              </div>

              {/* Languages */}
              <div className="row-primary">
                <h2>Languages</h2>
                <Controller
                  name="language_ids"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <div style={{ width: "100%" }} className="multiselect">
                        <AsyncPaginate
                          menuPlacement="auto"
                          isMulti
                          styles={{ ...selectCustomStyles }}
                          value={languageState}
                          loadOptions={filteredLanguagesLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(selected) => {
                            setLanguageState(selected);
                            field.onChange(selected.map((opt) => opt.value));
                          }}
                          additional={{ page: 1 }}
                        />
                      </div>
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex align-items-center justify-content-between px-3">
              <div className="row-primary row_10 w-100">
                <button type="submit" className="main_button green">
                  Search
                </button>
                <button
                  type="button"
                  className="main_button lightBlue"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ListLayout
        key={JSON.stringify(filters)}
        time={true}
        className={"appointment"}
        title=""
        buttonTitle=""
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        addButtonDisabled={true}
        totalPages={totalPages}
        fetchList={fetchExcelAppointments}
        filtration={filters}
        selectedItems={selectedItems}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loader={loader}
        searchPlaceholder="Search Excel Appointments ..."
        noOfItems={total}
        disableBootstrap={true}
        addformModal={false}
        styling="appointment"
      />
    </>
  );
}

const ExcelAppointmentItem = ({
  appointment,
  index,
  current_page,
  pageLength,
  checkPermission,
}) => {
  const typeLabels = {
    0: "Interpreting Request",
    1: "After Hours Request",
    2: "Bilingual Exams Request",
  };

  const rawDatetime = appointment?.date + " " + appointment?.start_time;
  const formattedDate = moment(rawDatetime, "YYYY-MM-DD HH:mm:ss").format(
    "MMM D, YYYY HH:mm:ss"
  );

  const serial = useMemo(() => {
    return index + 1 + (current_page - 1) * pageLength;
  }, [current_page, pageLength]);

  return (
    <tr>
      <td style={getBackgroundStyle(appointment?.status)}>
        <div className="row_check">
          <span>{serial}</span>
        </div>
      </td>
      <td style={getBackgroundStyle(appointment?.status)}>
        {checkPermission("Detail Appointments") ? (
          <Link
            className="link_preview"
            to={`/appointments/view/${appointment?.id}`}
          >
            {appointment?.appid}
          </Link>
        ) : (
          appointment?.appid
        )}
      </td>

      <td
        style={{
          ...getBackgroundStyle(appointment?.status),
          lineHeight: "1.5",
        }}
      >
        {appointment?.language?.name || appointment?.language_other}
      </td>
      <td style={getBackgroundStyle(appointment?.status)}>
        {typeLabels[appointment?.type] || ""}
      </td>

      <td style={getBackgroundStyle(appointment?.status)}>
        {appointment?.status}
      </td>

      <td style={getBackgroundStyle(appointment?.status)}>
        {appointment?.vendor_id != null && appointment?.vendor_interpreter
          ? `${appointment.vendor_interpreter?.first_name || ""} ${appointment.vendor_interpreter?.last_name || ""} - Excel`.trim()
          : appointment?.vendor_id != null
            ? `${appointment?.vendor_user?.name === 'Adelina Past' ? 'Adelina Past' : (appointment?.vendor_user?.name || 'Vendor')} (Excel)`
            : `${appointment?.interpreter?.first_name || ""} ${appointment?.interpreter?.last_name || ""}`.trim()
        }
      </td>
      <td style={getBackgroundStyle(appointment?.status)}>
        {appointment?.date ? formattedDate : "N/A"}
      </td>
      <td style={getBackgroundStyle(appointment?.status)}>
        {appointment?.school?.name || "N/A"}
      </td>

      <td style={getBackgroundStyle(appointment?.status)}>
        {moment(appointment.created_at, "YYYY-MM-DD HH:mm").format(
          "MMM D, YYYY HH:mm"
        )}
      </td>
    </tr>
  );
};

export default ExcelAppointments;
